import React, { useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { OrderItem } from "../../context/OrderContext";
import { useAuth } from "../../context/AuthContext";

interface RepeatCustomModalProps {
  show: boolean;
  onHide: () => void;
  itemName: string;
  existingCartItems: OrderItem[];
  onRepeat: (cartItem: OrderItem) => void;
  onCustomize: () => void;
  updateQuantity: (cartId: string, quantity: number) => void;
}

const RepeatCustomModal: React.FC<RepeatCustomModalProps> = ({
  show,
  onHide,
  itemName,
  existingCartItems,
  onRepeat,
  onCustomize,
  updateQuantity,
}) => {
  const { currencySymbol } = useAuth();
    useEffect(() => {
  if (existingCartItems.length === 0) {
    onHide();
  }
}, [existingCartItems.length, onHide]);
  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="!max-w-[640px]"
      contentClassName="!rounded-3xl bg-white p-0 shadow-lg"
    >
      <Modal.Body className="p-8">
        {/* Close Button */}
        <button
          onClick={onHide}
          className="absolute top-4 right-4 bg-black text-white rounded-full w-8 h-8 flex items-center justify-center dm-repeat-close"
          aria-label="Close"
        >
          ✕
        </button>

        {/* Header */}
        <div className="mb-1">
          <h2 className="text-sm font-medium text-black mb-1">
            Repeat previous customisation?
          </h2>
          <h3 className="text-2xl font-semibold text-black w-11/12">{itemName}</h3>
        </div>

        {/* Existing Cart Items */}
        <div className="space-y-4 mb-6">
          {existingCartItems.map((cartItem, index) => (
            <div
              key={cartItem.cartId}
              className="flex items-center justify-between bg-main/5 p-2 rounded-2xl"
            >
              <div className="flex items-center gap-4 flex-1">
                <img
                  src={cartItem.imageUrl || "/placeholder.png"}
                  alt={cartItem.name}
                  className="w-16 h-16 object-cover rounded-xl"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).src =
                      "/placeholder.png";
                  }}
                />
                <div className="flex-1">
                  <h4 className="text-base font-bold text-black mb-1">
                    {cartItem.name}
                  </h4>
                  {cartItem.customOptions && cartItem.customOptions.length > 0 ? (
                    <p className="text-sm text-gray-600 line-clamp-2">
                      Added: {cartItem.customOptions
                        .filter((opt) => opt.qty > 0)
                        .map((opt) => opt.label)
                        .join(", ")}
                    </p>
                  ) : (
                    <p ></p>
                  )}
                  <p className="text-lg font-bold text-black mt-1">
                    {currencySymbol}{Number(cartItem.price).toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Quantity Controls */}
              <div className="flex items-center gap-2 border-black/30 bg-white rounded-[10px] dm-repeat-qty">
                <button
                  onClick={() => {
                    if (cartItem.quantity === 1) {
                      // You might want to handle item removal here
                      updateQuantity(cartItem.cartId, 0);
                    } else {
                      updateQuantity(cartItem.cartId, cartItem.quantity - 1);
                    }
                  }}
                  className="w-8 h-8 rounded-lg bg-white font-bold flex items-center justify-center text-black "
                >
                  −
                </button>
                <span className="text-lg font-semibold text-black min-w-[30px] text-center">
                  {String(cartItem.quantity).padStart(2, "0")}
                </span>
                <button
                  onClick={() =>
                    updateQuantity(cartItem.cartId, cartItem.quantity + 1)
                  }
                  className="w-8 h-8 rounded-lg bg-white font-bold flex items-center justify-center text-black"
                >
                  +
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        {existingCartItems.length === 1 ? (
          // Single item - show "Customize" and "Repeat" buttons
          <div className="flex gap-4">
            <button
              onClick={onCustomize}
              className="dm-repeat-cta flex-1 py-3 px-6 rounded-2xl border-2 bg-main text-white font-semibold text-lg "
            >
              Customize
            </button>
            <button
              onClick={() => onRepeat(existingCartItems[0])}
              className="dm-repeat-cta flex-1 py-3 px-6 rounded-2xl bg-main text-white font-semibold text-lg"
            >
              Repeat
            </button>
          </div>
        ) : (
          // Multiple items - show only "Customize" button
          <button
            onClick={onCustomize}
            className="dm-repeat-cta w-full py-3 px-6 rounded-2xl border-2 border-black/30 bg-main text-white font-semibold text-lg "
          >
            Customize
          </button>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default RepeatCustomModal;
